import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { trackOnboardingCompletedFrontend } from '@codemerlin/shared/analytics';
import type {
  OnboardingSessionResponse,
  OnboardingSummary,
} from '@codemerlin/shared/types/onboarding';
import type { OnboardingPath } from '@codemerlin/shared/constants/onboarding';

export const ONBOARDING_SESSION_QUERY_KEY = ['onboarding-session'] as const;

/**
 * Organization setup Quickstart is owner-only (DC-479). The server is the sole
 * authority: `/onboarding/state` returns `quickstartRestricted: false` for the
 * owner and `true` for every other member. Anything else — still loading, a
 * failed request, or a mutation response written from the role-unaware
 * `mapToResponse` — counts as restricted, so setup content is never flashed to
 * an invited member.
 */
export function isQuickstartAllowed(session: OnboardingSessionResponse | undefined): boolean {
  return session?.quickstartRestricted === false;
}

export function useOnboardingSummary() {
  return useQuery({
    queryKey: ['onboarding-summary'] as const,
    queryFn: () => api.get<OnboardingSummary>('/onboarding/summary'),
    staleTime: 1000 * 60,
  });
}

export function useOnboardingSession() {
  const queryClient = useQueryClient();

  const sessionQuery = useQuery({
    queryKey: ONBOARDING_SESSION_QUERY_KEY,
    queryFn: () => api.get<OnboardingSessionResponse>('/onboarding/state'),
    staleTime: 1000 * 60, // 1 minute
  });

  // `/onboarding/start|state|complete` responses come from the org-scoped,
  // role-unaware `mapToResponse`, which omits `quickstartRestricted`. Writing
  // them verbatim would drop the owner-only posture from the cache and let a
  // non-owner back into setup until the next refetch (DC-479).
  const writeSessionCache = useCallback(
    (data: OnboardingSessionResponse) => {
      queryClient.setQueryData<OnboardingSessionResponse>(ONBOARDING_SESSION_QUERY_KEY, (prev) =>
        data.quickstartRestricted === undefined && prev?.quickstartRestricted !== undefined
          ? { ...data, quickstartRestricted: prev.quickstartRestricted }
          : data
      );
    },
    [queryClient]
  );

  const updateSessionMutation = useMutation({
    mutationFn: (data: { path: OnboardingPath }) =>
      api.post<OnboardingSessionResponse>('/onboarding/start', data),
    onSuccess: (data) => {
      writeSessionCache(data);
    },
  });

  const updateSuccessStateMutation = useMutation({
    mutationFn: (settings: { confettiSeen?: boolean; dismissedBanners?: string[] }) =>
      api.patch<OnboardingSessionResponse>('/onboarding/state', { settings }),
    onSuccess: (data) => {
      writeSessionCache(data);
    },
  });

  const completeOnboardingMutation = useMutation({
    mutationFn: (data?: {
      feature_preferences?: Record<string, unknown>;
      review_policies?: Record<string, unknown>;
      repository_ids?: string[];
    }) =>
      api.post<OnboardingSessionResponse & { dashboardUrl?: string }>(
        '/onboarding/complete',
        data ?? {}
      ),
    onSuccess: (data, variables) => {
      writeSessionCache(data);
      const repoCount = variables?.repository_ids?.length ?? 0;
      trackOnboardingCompletedFrontend({
        onboarding_path: data.path ?? 'unknown',
        onboarding_status: data.status,
        repository_count: repoCount,
        had_feature_payload: variables?.feature_preferences !== undefined,
      });
    },
  });

  return {
    session: sessionQuery.data,
    quickstartAllowed: isQuickstartAllowed(sessionQuery.data),
    isLoading: sessionQuery.isLoading,
    isError: sessionQuery.isError,
    error: sessionQuery.error,
    refetch: sessionQuery.refetch,
    updateSession: updateSessionMutation.mutate,
    isUpdating: updateSessionMutation.isPending,
    updateError: updateSessionMutation.error,
    updateSuccessState: updateSuccessStateMutation.mutate,
    isUpdatingSuccessState: updateSuccessStateMutation.isPending,
    completeOnboarding: completeOnboardingMutation.mutate,
    isCompleting: completeOnboardingMutation.isPending,
  };
}

/**
 * Quickstart visibility for callers that do not need the whole session.
 * Shares the `ONBOARDING_SESSION_QUERY_KEY` cache entry, so mounting this
 * costs no extra request wherever `OnboardingGuard` has already run.
 */
export function useQuickstartAllowed(): boolean {
  const { session } = useOnboardingSession();
  return isQuickstartAllowed(session);
}
